/* eslint-disable @typescript-eslint/no-use-before-define */
import { stringFromErrorOrAny } from "../utils/tools";
import { ConnectionInfo } from "./WebSocketActions";

/** Communication channel used for sending and receiving data from native code */
export class NativeCommChannel {
    private socket: WebSocket | null;
    private promises: { [id: number]: { resolve: (value?: any) => void; reject: (reason?: any) => void } } = {};
    private id: number = 0;
    private static ConnectTimeoutSec: number = 10;

    public async connect(eventHandler: PlatformEventHandler, info: ConnectionInfo): Promise<void> {
        return new Promise((resolve, reject) => {
            let isConnected: boolean = false;
            setTimeout(() => {
                if (!isConnected) {
                    reject(`WebSocket connection timed out after ${NativeCommChannel.ConnectTimeoutSec} seconds`);
                }
            }, NativeCommChannel.ConnectTimeoutSec * 1000);

        // eslint-disable-next-line @microsoft/sdl/no-insecure-url
        this.socket = new WebSocket("ws://127.0.0.1:" + info.port + "/feedback"); // TODO: pass in fsid on connect
            this.socket.onopen = (ev: Event): void => {
                isConnected = true;
                resolve();
            }

            this.socket.onerror = (ev: Event): void => {
                reject("WebSocket connection error");
                this.rejectAndDeleteExistingPromises("WebSocket connection error");
            }

            this.socket.onclose = (ev: CloseEvent): void => {
                eventHandler(PlatformEvent.disconnected);
                this.rejectAndDeleteExistingPromises(`WebSocket connection closed: ${ev.reason}`);
            }

            this.socket.onmessage = (msg: MessageEvent): void => {
                const id: number | null = null;
                let reject: any | null = null;
                try {
                    const data: MessageResponse = JSON.parse(msg.data);
                    if (typeof data.id === "number") { // if response to send(...) request
                        const id = data.id;

                        let resolve: any | null = null;
                        // eslint-disable-next-line security/detect-object-injection
                        const promises = this.promises[id];
                        resolve = promises.resolve;
                        reject = promises.reject;
                        if (data.error) {
                            console.error("FAILED: " + ((data.error as any)?.text ?? data.error));
                            reject(data.error);
                        }
                        else {
                            resolve(data.data);
                        }
                    }
                    else { // ... else platform event
                        eventHandler(data.id, data.data);
                    }
                }
                catch (err) { // critical error. example: can happen if the server sends malformed JSON
                    const errorText: string = stringFromErrorOrAny(err);
                    console.error("--WebSocket Server Critical Error--");
                    console.error(errorText);
                    console.log(msg.data);
                    if (reject) { // if we have valid reject handler
                        reject(errorText);
                    }
                    else {
                        this.rejectAndDeleteExistingPromises(errorText); // since critical error, reject everything
                    }
                }
                finally {
                    if (id) {
                        // eslint-disable-next-line security/detect-object-injection
                        delete this.promises[id];
                    }
                }
            }
        });
    }

    public async send(command: Command, data: CommandData): Promise<any> {
        const id: number = this.id;
        this.id++;
        return new Promise((resolve, reject) => {
            // eslint-disable-next-line security/detect-object-injection
            this.promises[id] = { resolve, reject };
            this.socket.send(JSON.stringify({
                id,
                command,
                data,
            }));
        });
    }

    private rejectAndDeleteExistingPromises(reason: string): void {
        for (const id in this.promises) {
            // eslint-disable-next-line security/detect-object-injection
            this.promises[id].reject(reason);
        }
    }
}

export type PlatformEventHandler = (event: PlatformEvent, data?: any) => void;

export enum Command {
    get = "get", // GET command from web API
    post = "post", // POST command to web API
    signOutAndIn = "signOutAndIn", // sign out, then sign in again; used when pressing "sign in" button
    signOut = "signOut", // sign out
    checkForSignIn = "checkForSignIn", // check for sign in, used at startup
    startVsInstance = "startVsInstance", // start new VS instance to show in the Record view
    getRunningVsInstances = "getRunningVsInstances", // get running instances for Record view
    updateSettings = "updateSettings",
    startCollectDiagnostics = "startCollectDiagnostics",
    stopCollectDiagnostics = "stopCollectDiagnostics",
    hello = "hello", // handshake on first connection to verify feedback session Id and get product (VS, VS Installer) settings
    viewFile = "viewFile", // request to view a file when user clicks on file name. Depending on file extension, will either open file or view in Explorer
    getScreenshot = "getScreenshot", // get screenshot image that was created when user clicked Report Problem. Not passed through settings to speed things up and allow for re-getting screenshot at some point later
    selectFiles = "selectFiles", // prompt user to select list of files from the native file system; used for "Attach additional files" functionality
    startUploadingFiles = "startUploadingFiles", // prepare (crop screenshot if needed) and start uploading files asynchronously

    /** Creates a new tracking Id and sends it to Nexus server session, if connected
        Used to support file upload from Nexus server by correlating trackingId with files to upload
        if the client-server Nexus connection breaks before the customer submits feedback.
     */
    reserveNewTrackingId = "reserveNewTrackingId",

    /** Chooses a specific account from the currently signed-in accounts to use for commands.
        Used after user selects account or switches account in DevCom UI. 
    */
    chooseAccount = "chooseAccount",
}

export enum PlatformEvent {
    disconnected = "disconnected", // never sent from server, the client will auto-generate it if server disconnects
    collectProcessing = "collectProcessing", // Show the user that we are now collecting additional info (dump + wrapping ETL)
    collectStatus = "collectStatus",  // Show the user status of collection
    collectDone = "collectDone",  // Show the user the ReportProblem dialog as recording is done
    signedIn = "signedIn", // may come from VS in case user signs in from VS
    signedOut = "signedOut",
    clientError = "clientError", // when client sends unexpected or invalid data
    serverSettings = "serverSettings", // never sent from server; client will auto-generate after server responds to "hello"
    fileCheck = "fileCheck", // Sent when server checks whether system files are available (exist on disk) - e.g. system logs are checked to exist after 5 seconds, and some are not present will trigger this event to show smaller list of system logs
    remoteFiles = "remoteFiles", // Sent in case of Nexus connection when we receive list of remote (Nexus server) system logs or recording files
}

export enum RecordingStatus {
    success = "success",
    error = "error", // if user kills collector process or it crashes
    cancel = "cancel",  // if user presses "No" on UAC prompt
}

// Code received from native for collectStatus event when it is safe to close VS while recording is still in progress
export const ActionRecorderStatusCanCloseVS: string = "STATUS_CAN_CLOSE_VS";

export interface CommandData {
}

export interface WebRequestCommandData {
    partialUri: string;
    data: any;
}

interface MessageResponse {
    id: number | PlatformEvent;
    data?: any;
    error?: string | { text: string; };
}
